/**
 * The Bitwright page.
 *
 * You type a sentence, a spec comes back, and this file draws it. Everything it
 * knows about the format comes from the same place the renderer does: the shape
 * table, the glyphs. Add a shape and it turns up here without this file
 * changing, which is the point of publishing the vocabulary as data.
 *
 * The drawing happens in the tab. The only thing that leaves is the sentence,
 * and only when you ask for one to be drawn.
 */

import {
	MOTIONS,
	MOTION_BRIEF,
	MOTION_LABEL,
	VIEWS,
	VIEW_BRIEF,
	VIEW_LABEL,
	checkWant,
	type Motion,
	type View,
} from "../src/ask.js";
import { GLYPHS, KITS, kitFrom, parseKit, type Kit } from "../src/kit.js";
import { bitmapOf, renderFrames } from "../src/render.js";
import { encodePng } from "../src/png.js";
import { encodeGif, spriteSheet } from "../src/gif.js";
import { SHAPES } from "../src/shapes.js";
import { SpecError, type Part, type SpriteSpec } from "../src/spec.js";
import { SpecBuilder } from "../src/stream.js";
import { fitScale, type Rgba } from "../src/raster.js";

// Registers the built-in kits as a side effect, which has to happen before
// anything renders.
import { EXAMPLES, asSpec } from "./bundled.js";
// Fills the [data-chrome="header"] bar as a side effect. The workbench uses the
// same one every other page does.
import "./chrome.js";
import { css, fromHex, hex } from "./colour.js";
import { blit } from "./paint.js";
import { closeVeil, openVeil, veilDrew, veilFailed, veilStuck, watchDraw } from "./veil.js";
import { buildReference } from "./vocab.js";
import { askText, askYesNo } from "./modal.js";
import { buildFields } from "./partedit.js";
import { DrawRefused, type DrawWatch, SHELF_MAX, readShelf, requestDraw, setUpBotCheck, writeShelf } from "./service.js";
import { afterDelete } from "./shelf.js";
import { type Shot, readShot } from "./shot.js";

/**
 * Things worth asking for.
 *
 * Deliberately not a menu of what Bitwright is good at. They are ordinary objects
 * with an odd detail attached, because the question the page has to answer in
 * one glance is "will it draw the thing in my head", and a list of tidy machines
 * answers the wrong question.
 */
const SUGGESTIONS = [
	"a purple fridge with a plant inside",
	"a brass diving helmet",
	"a stack of old library books",
	"a red toolbox with a hammer sticking out",
	"a potted cactus wearing a tiny hat",
];

/**
 * `out` and `replaces` record where a sprite belongs in one particular project
 * and which script it replaced. `approximates` is a note to a reviewer. All
 * three are bookkeeping rather than part of describing a sprite, so the format
 * still accepts them and this page never shows them.
 */
const MIGRATION_FIELDS = ["out", "replaces", "approximates"];

function tidy(spec: SpriteSpec): SpriteSpec {
	const clean = { ...spec } as Record<string, unknown>;
	for (const field of MIGRATION_FIELDS) delete clean[field];
	return asSpec(clean);
}

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;

const editor = $<HTMLTextAreaElement>("spec");
const canvas = $<HTMLCanvasElement>("preview");
const strip = $<HTMLDivElement>("strip");
const status = $<HTMLParagraphElement>("status");
const scaleInput = $<HTMLInputElement>("scale");
const playButton = $<HTMLButtonElement>("play");
const shapeList = $<HTMLDivElement>("shapes-grid");
const stackList = $<HTMLOListElement>("stack");
const tiles = $<HTMLUListElement>("tiles");
const roleList = $<HTMLUListElement>("roles");
const kitLine = $<HTMLSpanElement>("kitline");
const shelf = $<HTMLUListElement>("shelf");
const shelfCount = $<HTMLSpanElement>("shelfcount");
const wantInput = $<HTMLInputElement>("want");
const drawButton = $<HTMLButtonElement>("draw");
const drawNote = $<HTMLParagraphElement>("drawnote");
// Up here with the other elements rather than beside the saving code, because
// `rerender` refreshes it and runs long before that part of the file does.
const saveButton = $<HTMLButtonElement>("save");
let savedText = "";
// Only ever shown to a paid account, so everything reading it copes with absent.
const sizePick = document.getElementById("size") as HTMLSelectElement | null;

/*
 * The options start folded on a phone and open everywhere else.
 *
 * `open` is in the markup so the settings are visible with no script at all,
 * which is the right failure. Closing it is what needs a screen measurement,
 * and CSS cannot remove an attribute. Only on load: reopening it under somebody
 * who has just folded it away, because they turned their phone sideways, would
 * be the page arguing with them.
 */
const cmdMore = document.querySelector<HTMLDetailsElement>(".cmd-more");
if (cmdMore && window.matchMedia("(max-width: 68rem)").matches) cmdMore.open = false;

const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");

// ---- reading the spec ----------------------------------------------------------------

function readSpec(): SpriteSpec | null {
	try {
		return JSON.parse(editor.value) as SpriteSpec;
	} catch {
		return null;
	}
}

/** The kit a spec is drawn with, whether it named one or carried one. */
function kitOf(spec: SpriteSpec): Kit | null {
	try {
		return kitFrom(spec.kit);
	} catch {
		return null;
	}
}

const kitLabel = (spec: SpriteSpec) => (typeof spec.kit === "string" ? spec.kit : (spec.kit?.name ?? "custom"));

/**
 * Turn a named kit into a copy the sprite owns.
 *
 * The moment somebody drags a colour picker they are editing this sprite, not
 * every sprite that shares a palette. Named kits are registered globally, so
 * writing into one would silently restyle the examples too. Copying first keeps
 * the edit where it was aimed, and has the side effect of teaching the format's
 * better half: a spec that carries its own kit renders anywhere with no setup.
 */
function detachKit(spec: SpriteSpec): SpriteSpec {
	if (typeof spec.kit !== "string") return spec;
	const named = KITS[spec.kit];
	if (!named) return spec;
	return {
		...spec,
		kit: {
			name: named.name,
			roles: Object.fromEntries(Object.entries(named.roles).map(([k, v]) => [k, [...v] as Rgba])),
			ramps: Object.fromEntries(Object.entries(named.ramps).map(([k, v]) => [k, [...v]])),
			...(named.glyphs ? { glyphs: named.glyphs } : {}),
		},
	};
}

// ---- which roles a part asks for -------------------------------------------------

/**
 * Read a part's role names straight off the shape's own field schema.
 *
 * Hard-coding "the role is in the `role` field" would be wrong for half the
 * vocabulary: a lamp carries three in `roles`, a meter reaches a whole ramp.
 * Asking the schema means a new shape works here the day it is added.
 */
function rolesUsedBy(part: Part, kit: Kit): string[] {
	const shape = SHAPES[part.shape as string];
	if (!shape) return [];
	const found: string[] = [];
	for (const [field, def] of Object.entries(shape.fields)) {
		const value = part[field] ?? def.default;
		if (def.type === "role" && typeof value === "string" && value !== "none") found.push(value);
		if (def.type === "roles" && Array.isArray(value)) found.push(...(value as string[]));
		if (def.type === "ramp" && typeof value === "string") found.push(...(kit.ramps[value] ?? []));
	}
	return [...new Set(found)];
}

/** The swatch shown against a part in the stack: whatever it mostly draws in. */
function leadRole(part: Part, kit: Kit): string | null {
	return rolesUsedBy(part, kit)[0] ?? null;
}

/** A short human summary of where a part sits, for the stack rows. */
function whereOf(part: Part): string {
	if (Array.isArray(part.inset)) return `inset ${(part.inset as number[]).join(" ")}`;
	if (Array.isArray(part.at)) return `at ${(part.at as number[]).join(", ")}`;
	if (Array.isArray(part.from)) return `from ${(part.from as number[]).join(", ")}`;
	return "";
}

// ---- drawing -----------------------------------------------------------------------

/**
 * A sprite on a canvas, with the editing grid over it if that is switched on.
 *
 * The drawing itself is shared with the welcome page. What is not shared is
 * this: the grid is a setting belonging to the canvas somebody is editing, and
 * the overlay that shows a sprite being drawn has no business reading it.
 */
function blitWithGrid(
	target: HTMLCanvasElement,
	source: { width: number; height: number; data: Uint8Array },
	scale: number,
	times = 1,
) {
	blit(target, source, scale, times);
	const ctx = target.getContext("2d");
	// The pixel grid is for placing pixels, and a repeat is for judging a seam.
	// Drawn over nine copies it is nine times the lines and hides the one join
	// you are looking at.
	if (ctx && grid && times === 1) drawGrid(ctx, source.width, source.height, scale);
}

/**
 * One line per pixel boundary, so you can see the grid you are drawing on.
 *
 * Hidden below 4x because at 1x the lines would be the picture, and drawn in a
 * translucent light and a translucent dark on top of each other so it shows on
 * a black sprite and on a white one without a setting to choose between them.
 *
 * Every tenth line is stronger. Counting to 32 across a canvas of identical
 * squares is exactly the thing a grid is supposed to save you from.
 */
const GRID_FROM = 4;

function drawGrid(ctx: CanvasRenderingContext2D, wide: number, high: number, scale: number) {
	if (scale < GRID_FROM) return;
	ctx.save();
	// Half pixel offsets, or a one pixel line lands across two device pixels and
	// comes out two pixels wide and grey.
	ctx.translate(0.5, 0.5);
	for (const [colour, every] of [["rgb(0 0 0 / 0.28)", 1] as const, ["rgb(255 255 255 / 0.22)", 1] as const]) {
		ctx.strokeStyle = colour;
		ctx.lineWidth = 1;
		ctx.beginPath();
		for (let x = every; x < wide; x += every) {
			ctx.moveTo(x * scale - 1, -1);
			ctx.lineTo(x * scale - 1, high * scale);
		}
		for (let y = every; y < high; y += every) {
			ctx.moveTo(-1, y * scale - 1);
			ctx.lineTo(wide * scale, y * scale - 1);
		}
		ctx.stroke();
	}
	ctx.strokeStyle = "rgb(120 200 230 / 0.5)";
	ctx.beginPath();
	for (let x = 8; x < wide; x += 8) {
		ctx.moveTo(x * scale - 1, -1);
		ctx.lineTo(x * scale - 1, high * scale);
	}
	for (let y = 8; y < high; y += 8) {
		ctx.moveTo(-1, y * scale - 1);
		ctx.lineTo(wide * scale, y * scale - 1);
	}
	ctx.stroke();
	ctx.restore();
}

// ---- state -----------------------------------------------------------------------

/*
 * The grid, remembered.
 *
 * Somebody who wants to see the pixels wants to see them on the next sprite
 * too, and a toggle that resets every time is a toggle people stop using.
 */
let grid = localStorage.getItem("bitwright.grid") === "1";

/*
 * Show the sprite repeated, remembered the same way.
 *
 * A tile judged as a single image is how bad tiles get shipped: the seam only
 * exists between two copies, so looking at one copy cannot show it to you. This
 * is also the only place the `tile` flag becomes visible, since a sprite with it
 * off looks identical alone and grows a grid the moment it repeats.
 */
let repeat = localStorage.getItem("bitwright.repeat") === "1";
let frames: ReturnType<typeof renderFrames> = [];
let current = 0;
let playing = !reduced.matches;
let disabled = new Set<number>();
let isolated: number | null = null;

/**
 * While a new sprite is assembling this is how many parts are drawn so far.
 *
 * Watching it build is the one explanation that does not need a paragraph: the
 * parts are ordered, and a later one covers an earlier one. It plays once when
 * a sprite arrives and then gets out of the way.
 */
const STEP_MS = 260;
let assembleTo: number | null = null;
let assembleNext = 0;

function startAssembly(parts: number) {
	if (reduced.matches || parts < 2) {
		assembleTo = null;
		return;
	}
	assembleTo = 1;
	assembleNext = performance.now() + STEP_MS;
}

/** The spec actually drawn: assembly wins, then isolate, then parts switched off. */
function effectiveSpec(spec: SpriteSpec): SpriteSpec {
	// Keep the frames block while assembling, and just show the first frame.
	//
	// Dropping it to force a still is the same mistake that once emptied half the
	// shelf: a part whose colour is driven by a frame throws the moment the drive
	// it names stops existing. Here it was worse, because the throw happened
	// inside the animation loop and killed it outright, so an animated sprite
	// froze part-built with no way back.
	if (assembleTo !== null) return { ...spec, parts: spec.parts.slice(0, assembleTo) };
	if (isolated !== null) return { ...spec, parts: [spec.parts[isolated]].filter(Boolean) };
	if (!disabled.size) return spec;
	return { ...spec, parts: spec.parts.filter((_, i) => !disabled.has(i)) };
}

const problem = $<HTMLDivElement>("problem");

/**
 * Say what went wrong, where the person is looking.
 *
 * The validator's message is the most useful sentence this tool produces: it
 * names the mistake and lists what would have been accepted instead. It gets
 * shown three times over, because someone editing the text at the bottom of the
 * page cannot see a caption under a picture at the top of it.
 */
function fail(message: string) {
	status.dataset.state = "bad";
	status.textContent = message;
	status.title = `${message}

Click to open the Data panel.`;
	problem.hidden = false;
	problem.replaceChildren();
	const head = document.createElement("b");
	head.textContent = "That did not draw";
	problem.append(head, message);
}

function clearProblem() {
	problem.hidden = true;
}

function rerender(rebuild = true) {
	// Every path into here follows the spec changing, including the ones that
	// bail out below, so the save button is refreshed before any of them can.
	refreshSave();
	const spec = readSpec();
	if (!spec) {
		try {
			JSON.parse(editor.value);
		} catch (err) {
			return fail(`That is not valid JSON. ${(err as Error).message}`);
		}
		return;
	}
	/*
	 * Before the render, so it is right even when the render is not.
	 *
	 * A spec that parses but does not draw used to leave these showing the
	 * previous sprite: the JSON said "untitled" and the name field still said
	 * "mug", which reads as the field being broken rather than the sprite being
	 * wrong. They come from the spec, not from the drawing, so they can be
	 * correct either way.
	 */
	showSpriteHead(spec);

	try {
		frames = renderFrames(effectiveSpec(spec));
	} catch (err) {
		// A SpecError is the author's data being wrong and already reads as a
		// sentence naming what was allowed. Anything else is a bug in here.
		const e = err as Error;
		return fail(e instanceof SpecError || e.message.startsWith("unknown ") ? e.message : `Renderer error: ${e.message}`);
	}
	status.dataset.state = "ok";
	status.title = "";
	clearProblem();
	const [w, h] = spec.size;
	const off = disabled.size ? `, ${disabled.size} part${disabled.size === 1 ? "" : "s"} off` : "";
	matchName.textContent = `(${spec.name})`;
	status.textContent = `${spec.name} · ${w}×${h} · ${spec.parts.length} parts · ${frames.length} frame${frames.length === 1 ? "" : "s"} · kit ${kitLabel(spec)}${off}`;
	if (current >= frames.length) current = 0;

	const kit = kitOf(spec);
	if (kit) {
		if (rebuild) buildRoles(spec, kit);
		lightRoles(spec, kit);
	}
	// Rebuilding the stack while a pointer is over one of its rows destroys the
	// node that would have fired pointerleave, so isolate sticks on forever.
	// Hover redraws the sprite only; the stack is rebuilt when the spec changes.
	if (rebuild) {
		buildStack(spec, kit);
		buildTiles(spec, kit);
	}
	markAssembly();
	buildStrip();
	refreshView();
}

function refreshView() {
	const f = frames[current];
	if (f) {
		blitWithGrid(canvas, f.canvas, Number(scaleInput.value), repeat ? 3 : 1);
	}
	for (const [i, b] of [...strip.children].entries()) b.setAttribute("aria-pressed", String(i === current));
}

function buildStrip() {
	strip.replaceChildren();
	const animated = frames.length > 1;
	sheetButton.hidden = !animated;
	gifButton.hidden = !animated;
	if (!animated || assembleTo !== null) return;
	frames.forEach((f, i) => {
		const button = document.createElement("button");
		button.className = "frame";
		button.setAttribute("aria-label", `Frame ${i + 1} of ${frames.length}`);
		button.setAttribute("aria-pressed", String(i === current));
		const c = document.createElement("canvas");
		blitWithGrid(c, f.canvas, 2);
		button.append(c);
		button.addEventListener("click", () => {
			playing = false;
			playButton.textContent = "Play";
			current = i;
			refreshView();
		});
		strip.append(button);
	});
}

// ---- the build log --------------------------------------------------------------------

/**
 * The stack, pulled apart: one plate per part, cascading back.
 *
 * This is the page's argument. "A sprite is an ordered stack and a later part
 * covers an earlier one" takes a paragraph to write and no time at all to see
 * once the parts are separated and numbered.
 */
function buildTiles(spec: SpriteSpec, kit: Kit | null) {
	tiles.replaceChildren();

	/*
	 * Every part, not the first fourteen.
	 *
	 * There used to be a cap here with a "+14 more" at the end, back when this
	 * row was an illustration of the idea and running off the page was the only
	 * risk worth managing. It is now the visual index into a list you can edit,
	 * and a part with no preview is a part you cannot find. The row scrolls
	 * inside itself instead, so a 28 part sprite costs three rows of height
	 * rather than five. See #tiles in workbench.css.
	 */
	spec.parts.forEach((part, i) => {
		const li = document.createElement("li");
		const tile = document.createElement("button");
		tile.type = "button";
		tile.className = "tile checker";
		tile.dataset.on = "1";
		tile.dataset.off = disabled.has(i) ? "1" : "0";

		const c = document.createElement("canvas");
		try {
			// Rendered with the sprite's own frames kept, so a part whose colour is
			// driven by one still resolves rather than throwing on a missing drive.
			const frames = renderFrames({ ...spec, parts: [part] });
			// A pose is only drawn on its own frames, so show it on the first of them.
			const frame = frames[Array.isArray(part.on) ? (part.on[0] as number) : 0] ?? frames[0];
			const scale = Math.max(1, Math.floor(54 / Math.max(frame.canvas.width, frame.canvas.height)));
			blitWithGrid(c, frame.canvas, scale);
		} catch {
			// One unrenderable part must not take the whole row down with it.
		}
		const idx = document.createElement("span");
		idx.className = "idx";
		idx.textContent = String(i + 1).padStart(2, "0");
		tile.append(c, idx);

		const role = kit ? leadRole(part, kit) : null;
		tile.title = role ? `${i + 1}. ${part.shape}, drawn in ${role}` : `${i + 1}. ${part.shape}`;
		tile.setAttribute("aria-label", tile.title);

		const show = () => {
			if (assembleTo !== null) return;
			isolated = i;
			rerender(false);
		};
		const unshow = () => {
			if (isolated === null) return;
			isolated = null;
			rerender(false);
		};
		tile.addEventListener("pointerenter", show);
		tile.addEventListener("pointerleave", unshow);
		tile.addEventListener("focus", show);
		tile.addEventListener("blur", unshow);
		/*
		 * A tile opens the part, the same as the row it stands for.
		 *
		 * It used to switch the part off, which was fine while that was also what
		 * the row did. Now that a row opens the editor, two things that look like
		 * the same part doing opposite things is a trap. Hiding lives on the dot
		 * beside the row, and nowhere else.
		 */
		tile.addEventListener("click", () => {
			editing = editing === i ? null : i;
			isolated = null;
			rerender();
			document.querySelector(".part-edit")?.scrollIntoView({ block: "nearest" });
		});
		li.append(tile);
		tiles.append(li);
	});
}

// A stuck isolate leaves the sprite looking broken with no way to tell why, and
// a pointerleave on a tile is missed whenever that tile is rebuilt under the
// cursor. The container catches what the tiles drop.
tiles.addEventListener("pointerleave", () => {
	if (isolated === null) return;
	isolated = null;
	rerender(false);
});

function markAssembly() {
	const upTo = assembleTo ?? Number.MAX_SAFE_INTEGER;
	for (const [i, tile] of [...tiles.querySelectorAll<HTMLElement>(".tile")].entries()) {
		tile.dataset.on = i < upTo ? "1" : "0";
	}
}

// ---- the kit editor ---------------------------------------------------------------

/** Dim the roles this sprite never actually asks for. */
function lightRoles(spec: SpriteSpec, kit: Kit) {
	const used = new Set(spec.parts.flatMap((p) => rolesUsedBy(p, kit)));
	if (typeof spec.outline === "string") used.add(spec.outline);
	else if (spec.outline !== false) used.add("outline");
	for (const row of roleList.querySelectorAll<HTMLElement>(".role")) {
		row.dataset.lit = used.has(row.dataset.role ?? "") ? "1" : "0";
	}
}

function setRole(name: string, value: Rgba) {
	const spec = detachKit(readSpec() ?? ({} as SpriteSpec));
	if (typeof spec.kit === "string") return;
	spec.kit.roles[name] = value;
	setSpec(spec, true, false);
}

const paletteRead = $<HTMLParagraphElement>("paletteread");

/** Names live under the ribbon rather than beside every swatch. */
function readOut(name: string, colour: Rgba) {
	paletteRead.innerHTML = "";
	const b = document.createElement("b");
	b.textContent = name;
	paletteRead.append(b, ` — ${hex(colour)}`);
}

function buildRoles(spec: SpriteSpec, kit: Kit) {
	roleList.replaceChildren();
	for (const [name, colour] of Object.entries(kit.roles)) {
		const li = document.createElement("li");
		li.className = "role-row";
		li.dataset.role = name;
		li.addEventListener("pointerenter", () => readOut(name, colour));

		const swatch = document.createElement("input");
		swatch.type = "color";
		swatch.value = hex(colour);
		swatch.title = `${name} — ${hex(colour)}`;
		swatch.setAttribute("aria-label", `${name}, currently ${hex(colour)}. Choose a new colour.`);
		swatch.addEventListener("focus", () => readOut(name, colour));
		// `input` rather than `change`, so dragging the picker redraws live: seeing
		// the sprite move under the cursor is the whole argument for roles.
		swatch.addEventListener("input", () => setRole(name, fromHex(swatch.value, colour[3])));

		const drop = document.createElement("button");
		drop.type = "button";
		drop.className = "drop";
		drop.textContent = "×";
		drop.title = `Remove ${name}`;
		drop.setAttribute("aria-label", `Remove the colour ${name}`);
		drop.addEventListener("click", () => {
			const now = detachKit(readSpec() ?? ({} as SpriteSpec));
			if (typeof now.kit === "string") return;
			delete now.kit.roles[name];
			setSpec(now, true, false);
		});

		li.append(swatch, drop);
		roleList.append(li);
	}
	const count = Object.keys(kit.roles).length;
	kitLine.textContent = `${count} colours`;
	paletteRead.textContent =
		typeof spec.kit === "string"
			? `This sprite uses a shared palette called ${kit.name}. Change any swatch and it becomes this sprite's own copy.`
			: `This palette belongs to this sprite alone, so changing it affects nothing else.`;
}

$<HTMLButtonElement>("addrole").addEventListener("click", async () => {
	const spec = readSpec();
	const taken = spec ? Object.keys(kitOf(spec)?.roles ?? {}) : [];
	const name = await askText({
		title: "Add a colour",
		body: "Name it after the material, not the colour. A role called rim survives being repainted; a role called grey does not.",
		label: "Role name",
		placeholder: "rim",
		confirm: "Add it",
		check: (v) =>
			!/^[a-z][a-z0-9_]*$/.test(v)
				? "Lower case letters, numbers and underscores, starting with a letter."
				: taken.includes(v)
					? `This palette already has a ${v}.`
					: null,
	});
	if (name) setRole(name, [200, 200, 200, 255]);
});

$<HTMLButtonElement>("savekit").addEventListener("click", () => {
	const spec = readSpec();
	const kit = spec ? kitOf(spec) : null;
	if (!kit) return;
	const blob = new Blob([JSON.stringify(kit, null, "\t")], { type: "application/json" });
	const a = document.createElement("a");
	a.href = URL.createObjectURL(blob);
	a.download = `${kit.name}.json`;
	a.click();
	URL.revokeObjectURL(a.href);
});

// ---- the stack ---------------------------------------------------------------------

/*
 * THE NAME AND THE CANVAS.
 *
 * Both live in the spec beside `parts`, and both were reachable only by editing
 * the JSON. That was tolerable while every sprite came fully formed from the
 * model, and stopped being so the moment you could start a blank one: a canvas
 * you cannot resize is a 32 by 32 canvas.
 *
 * Written back on `change` rather than `input`, unlike the part fields. Halfway
 * through typing "48" the value is 4, and a one pixel canvas throws out every
 * inset in the sprite on its way past.
 */
const spriteName = $<HTMLInputElement>("spritename");
const spriteW = $<HTMLInputElement>("spritew");
const spriteH = $<HTMLInputElement>("spriteh");

function showSpriteHead(spec: SpriteSpec) {
	// Skipped while a field has focus, or resizing would overwrite the number
	// somebody is halfway through typing.
	if (document.activeElement === spriteName) return;
	if (document.activeElement === spriteW || document.activeElement === spriteH) return;
	spriteName.value = spec.name ?? "";
	spriteW.value = String(spec.size?.[0] ?? "");
	spriteH.value = String(spec.size?.[1] ?? "");
}

function editSpriteHead() {
	const spec = readSpec();
	if (!spec) return fail("Fix the JSON before changing the name or the size.");
	const w = Math.round(Number(spriteW.value));
	const h = Math.round(Number(spriteH.value));
	if (!Number.isFinite(w) || !Number.isFinite(h) || w < 1 || h < 1 || w > 512 || h > 512) {
		return fail("Width and height are whole pixels from 1 to 512.");
	}
	setSpec({ ...spec, name: spriteName.value.trim() || spec.name, size: [w, h] }, true);
}

for (const field of [spriteName, spriteW, spriteH]) {
	field.addEventListener("change", editSpriteHead);
}

/**
 * Which part is open for editing, or null.
 *
 * Held by index, which is the only stable handle a part has: the format gives
 * them no ids, and their position IS their meaning, because the stack is drawn
 * in order. Every operation that moves or removes a part fixes this up.
 */
let editing: number | null = null;

/**
 * The two bits of the open row that an edit can change, so they can be updated
 * without rebuilding the list. See `replacePart`.
 */
let openRow: { where: HTMLElement; fill: HTMLElement } | null = null;

/**
 * Write one part back and redraw. Every edit in the panel goes through here.
 *
 * Deliberately does NOT rebuild the stack. Rebuilding replaces the controls
 * with new ones, and typing the second digit of a number would land nowhere
 * because the box being typed into no longer exists. The one row that can go
 * stale is patched by hand instead.
 */
function replacePart(i: number, part: Part | null) {
	const spec = readSpec();
	if (!spec) return fail("Fix the JSON before editing a part.");
	const parts = [...spec.parts];
	if (part === null) parts.splice(i, 1);
	else parts[i] = part;

	const gone = part === null;
	if (gone) {
		// Everything above the removed part slides down one, and the on/off flags
		// are held by index, so without this the wrong part goes dark.
		disabled = new Set([...disabled].filter((n) => n !== i).map((n) => (n > i ? n - 1 : n)));
	}
	setSpec({ ...spec, parts }, true, gone);

	if (gone || !openRow) return;
	openRow.where.textContent = whereOf(part);
	const kit = kitOf({ ...spec, parts } as SpriteSpec);
	const role = kit ? leadRole(part, kit) : null;
	openRow.fill.style.background = role && kit?.roles[role] ? css(kit.roles[role]) : "transparent";
}

/** Move a part up or down the stack, which is what changes what covers what. */
function movePart(i: number, by: number) {
	const spec = readSpec();
	if (!spec) return fail("Fix the JSON before moving a part.");
	const to = i + by;
	if (to < 0 || to >= spec.parts.length) return;
	const parts = [...spec.parts];
	[parts[i], parts[to]] = [parts[to], parts[i]];
	editing = to;
	// The on/off flags are held by index too, so swapping the parts without
	// swapping those would switch off whichever part landed in the slot.
	const wasOff = disabled.has(i);
	const otherOff = disabled.has(to);
	disabled.delete(i);
	disabled.delete(to);
	if (wasOff) disabled.add(to);
	if (otherOff) disabled.add(i);
	setSpec({ ...spec, parts }, true);
}

/** The stack: the format turned into the interface, in draw order. */
function buildStack(spec: SpriteSpec, kit: Kit | null) {
	stackList.replaceChildren();
	openRow = null;
	if (editing !== null && editing >= spec.parts.length) editing = null;

	spec.parts.forEach((part, i) => {
		const li = document.createElement("li");
		li.className = "part-row";

		const b = document.createElement("button");
		b.type = "button";
		b.className = "part";
		b.dataset.off = disabled.has(i) ? "1" : "0";
		b.setAttribute("aria-expanded", String(editing === i));

		const n = document.createElement("span");
		n.className = "n";
		n.textContent = String(i + 1).padStart(2, "0");

		const chip = document.createElement("span");
		chip.className = "chip-c";
		const fill = document.createElement("i");
		const role = kit ? leadRole(part, kit) : null;
		if (role && kit?.roles[role]) fill.style.background = css(kit.roles[role]);
		chip.append(fill);

		const what = document.createElement("span");
		what.className = "what";
		what.textContent = part.shape as string;

		const where = document.createElement("span");
		where.className = "where";
		where.textContent = whereOf(part);

		b.append(n, chip, what, where);
		b.title = role ? `${part.shape}, drawn in ${role}. Click to edit it.` : String(part.shape);

		/*
		 * Two things a row can do, so they get a control each.
		 *
		 * Clicking the row used to switch the part off, which was the only thing
		 * the panel could do to a part and so had the whole row to itself. Now that
		 * clicking opens the editor, hiding needs its own button rather than a
		 * modifier key nobody would find.
		 */
		const eye = document.createElement("button");
		eye.type = "button";
		eye.className = "part-eye";
		const off = disabled.has(i);
		eye.dataset.off = off ? "1" : "0";
		eye.setAttribute("aria-pressed", String(!off));
		eye.title = off ? "Draw this part" : "Hide this part";
		eye.setAttribute("aria-label", `${off ? "Draw" : "Hide"} part ${i + 1}, ${part.shape}`);
		eye.textContent = off ? "○" : "●";
		eye.addEventListener("click", () => {
			isolated = null;
			if (off) disabled.delete(i);
			else disabled.add(i);
			rerender();
		});

		const show = () => {
			if (assembleTo !== null) return;
			isolated = i;
			rerender(false);
		};
		const unshow = () => {
			if (isolated === null) return;
			isolated = null;
			rerender(false);
		};
		li.addEventListener("pointerenter", show);
		li.addEventListener("pointerleave", unshow);
		b.addEventListener("focus", show);
		b.addEventListener("blur", unshow);
		b.addEventListener("click", () => {
			editing = editing === i ? null : i;
			isolated = null;
			rerender();
		});

		li.append(b, eye);
		stackList.append(li);

		// The list is laid out column-reverse, so part 01 sits at the bottom the
		// way a stack of paint does. That flips this too: to appear UNDER the row
		// it belongs to, the editor has to come BEFORE it in the markup.
		if (editing === i && kit) {
			openRow = { where, fill };
			li.before(editorFor(i, part, kit));
		}
	});
}

/**
 * The panel that opens under the part you clicked.
 *
 * Under the row rather than in a separate pane, so the thing being edited and
 * its place in the stack stay next to each other. The controls come from the
 * shape's field table; what is written here is everything around them.
 */
function editorFor(i: number, part: Part, kit: Kit): HTMLElement {
	const box = document.createElement("li");
	box.className = "part-edit";

	const shape = SHAPES[part.shape as string];
	if (!shape) {
		const unknown = document.createElement("p");
		unknown.className = "note";
		unknown.textContent = `${part.shape} is not a shape this build knows about, so it can only be edited in the Data panel.`;
		box.append(unknown);
		return box;
	}

	box.append(buildFields({ part, fields: shape.fields, kit, onChange: (next) => replacePart(i, next) }));

	const tools = document.createElement("div");
	tools.className = "part-tools";

	const move = (label: string, by: number, title: string) => {
		const b = document.createElement("button");
		b.type = "button";
		b.className = "btn tiny";
		b.textContent = label;
		b.title = title;
		b.addEventListener("click", () => movePart(i, by));
		return b;
	};

	const copy = document.createElement("button");
	copy.type = "button";
	copy.className = "btn tiny";
	copy.textContent = "Duplicate";
	copy.title = "Add a copy of this part directly above it";
	copy.addEventListener("click", () => {
		const spec = readSpec();
		if (!spec) return fail("Fix the JSON before duplicating a part.");
		const parts = [...spec.parts];
		parts.splice(i + 1, 0, structuredClone(part));
		editing = i + 1;
		setSpec({ ...spec, parts }, true);
	});

	const drop = document.createElement("button");
	drop.type = "button";
	drop.className = "btn tiny danger";
	drop.textContent = "Delete";
	drop.title = "Remove this part from the sprite";
	drop.addEventListener("click", () => {
		editing = null;
		replacePart(i, null);
	});

	tools.append(
		move("↑ Later", 1, "Draw this part after the one above, so it covers more"),
		move("↓ Earlier", -1, "Draw this part before the one below, so it covers less"),
		copy,
		drop,
	);
	box.append(tools);
	return box;
}

// A backstop on the container, because a pointerleave on a row can be missed
// if that row is replaced, and a stuck isolate would leave the sprite looking
// broken with no way to tell why.
stackList.addEventListener("pointerleave", () => {
	if (isolated === null) return;
	isolated = null;
	rerender(false);
});

// ---- editing ---------------------------------------------------------------------------

/**
 * Keep short arrays and whole parts on one line.
 *
 * `JSON.stringify(v, null, "\t")` puts every number of an inset on its own line,
 * which turns a readable eight-line sprite into two hundred lines of scrolling.
 * These are meant to be edited by hand.
 */
function format(value: unknown, indent = ""): string {
	if (Array.isArray(value) && value.every((v) => typeof v === "number" || typeof v === "string")) {
		return JSON.stringify(value).replace(/,/g, ", ");
	}
	if (Array.isArray(value)) {
		return `[\n${value.map((v) => `${indent}\t${format(v, `${indent}\t`)}`).join(",\n")}\n${indent}]`;
	}
	if (value && typeof value === "object") {
		const body = Object.entries(value).map(([k, v]) => `${JSON.stringify(k)}: ${format(v, `${indent}\t`)}`);
		const oneLine = `{ ${body.join(", ")} }`;
		if (oneLine.length <= 96 && !oneLine.includes("\n")) return oneLine;
		return `{\n${body.map((b) => `${indent}\t${b}`).join(",\n")}\n${indent}}`;
	}
	return JSON.stringify(value);
}

/**
 * Pick a zoom that fills the stage.
 *
 * A fixed zoom is wrong in a workbench: the same setting that suits a 64px
 * machine leaves a 32px item as a stamp in the middle of a very large grey
 * rectangle. Whole numbers only, because a fractional scale on pixel art is
 * the one thing that must never happen.
 */
function fitZoom(spec: SpriteSpec) {
	const stage = document.querySelector(".stage");
	if (!stage) return;
	const box = stage.getBoundingClientRect();
	const room = 0.82;
	const [w, h] = spec.size;
	if (!w || !h) return;
	scaleInput.value = String(fitScale(w, h, (box.width || 600) * room, (box.height || 400) * room, 16));
}

function setSpec(spec: SpriteSpec, keepState = false, rebuild = true) {
	editor.value = format(tidy(spec));
	if (!keepState) {
		current = 0;
		disabled = new Set();
		isolated = null;
		// Only on a new sprite. Refitting while somebody edits would undo a zoom
		// they deliberately set two keystrokes ago.
		fitZoom(spec);
	}
	rerender(rebuild);
}

// ---- asking for a sprite ------------------------------------------------------------

// ---- which way it faces -------------------------------------------------------------

let view: View = "front";
let motion: Motion = "auto";

const matchOpen = $<HTMLInputElement>("matchopen");
const matchName = $<HTMLElement>("matchname");

// ---- uploading a reference: pixel art to copy, or a photo to work from ------------

/**
 * Deciding what an image is lives in shot.ts, shared with the welcome page,
 * which runs the same classification as a demonstration. Only the showing of it
 * is here.
 */
let shot: Shot | null = null;

const uploadFile = $<HTMLInputElement>("uploadfile");
const uploadHas = $<HTMLDivElement>("uploadhas");
const uploadView = $<HTMLCanvasElement>("uploadview");
const uploadMeta = $<HTMLSpanElement>("uploadmeta");
const uploadHint = $<HTMLSpanElement>("uploadhint");

function clearUpload() {
	shot = null;
	uploadHas.hidden = true;
	uploadFile.value = "";
	uploadHint.hidden = false;
}

async function takeImage(file: File) {
	const read = await readShot(file);
	shot = read;

	// shot.ts prepares the thumbnail, because getting it right differs by kind
	// and both pages want the same answer.
	blitWithGrid(uploadView, read.thumb, 1);

	uploadMeta.textContent =
		read.kind === "sprite"
			? `${read.name} · sprite · ${read.width}×${read.height} · ${read.palette.length} colours`
			: `${read.name} · photo · ${read.width}×${read.height}`;
	uploadHas.hidden = false;
	uploadHint.hidden = true;
	drawNote.dataset.state = "";
	drawNote.textContent =
		read.kind === "sprite"
			? "Pixel art loaded. The next sprite will copy its angle, scale and exact colours."
			: "Photo loaded. The next sprite will be designed from the object in it, not copied pixel for pixel. Describe what you want.";
}



/** A .json is a spec, so it opens rather than becoming a reference image. */
async function takeSpec(file: File) {
	const parsed = JSON.parse(await file.text()) as SpriteSpec;
	renderFrames(parsed);
	startAssembly(parsed.parts.length);
	setSpec(parsed);
	shelve(parsed, file.name.replace(/\.json$/i, ""));
	drawNote.dataset.state = "";
	drawNote.textContent = `Opened ${file.name}. It is on your shelf, and you can tick the box above to match it.`;
}

uploadFile.addEventListener("change", async () => {
	const file = uploadFile.files?.[0];
	if (!file) return;
	try {
		// readShot enforces the size limit for images and says so with the actual
		// number. A spec is text and never approaches it.
		if (file.name.toLowerCase().endsWith(".json")) await takeSpec(file);
		else await takeImage(file);
	} catch (err) {
		clearUpload();
		drawNote.dataset.state = "bad";
		drawNote.textContent = err instanceof Error ? err.message : "That file could not be read.";
	}
});

$<HTMLButtonElement>("uploadclear").addEventListener("click", clearUpload);

/**
 * A row of choices where exactly one is on.
 *
 * Angle and motion were two copies of the same twenty lines, differing only in
 * which list they read and which variable they set. Written once, so a third
 * one costs a line rather than a paste, and so the two cannot drift apart in
 * how they mark the chosen option.
 */
function buildSegments<T extends string>(
	id: string,
	options: readonly T[],
	label: Record<T, string>,
	brief: Record<T, string>,
	chosen: T,
	pick: (option: T) => void,
) {
	const row = $<HTMLDivElement>(id);
	for (const option of options) {
		const b = document.createElement("button");
		b.type = "button";
		b.className = "seg-btn";
		b.textContent = label[option];
		b.title = brief[option];
		b.setAttribute("aria-pressed", String(option === chosen));
		b.addEventListener("click", () => {
			pick(option);
			for (const other of row.querySelectorAll<HTMLButtonElement>(".seg-btn")) {
				other.setAttribute("aria-pressed", String(other === b));
			}
		});
		row.append(b);
	}
}

buildSegments("views", VIEWS, VIEW_LABEL, VIEW_BRIEF, view, (option) => {
	view = option;
});
buildSegments("motions", MOTIONS, MOTION_LABEL, MOTION_BRIEF, motion, (option) => {
	motion = option;
});

let asking = false;

async function askFor(raw: string) {
	if (asking) return;
	const checked = checkWant(raw);
	if (!checked.ok) {
		// Saying nothing here is how a button ends up looking broken.
		drawNote.dataset.state = "bad";
		drawNote.textContent = checked.why;
		wantInput.focus();
		return;
	}
	const want = checked.want;
	asking = true;
	drawButton.disabled = true;
	drawNote.dataset.state = "busy";
	drawNote.textContent = `Drawing ${want}…`;
	/*
	 * Do not perform a draw we already know will be refused.
	 *
	 * The overlay opens before the request goes out, and the request waits on a
	 * bot-check token that can take seconds to arrive. So an empty account got a
	 * drawing card with a moving meter for the whole of that wait, and only then
	 * the refusal. Everything on screen said work was happening and none was.
	 *
	 * The balance is already known here, so an answer we can give instantly
	 * should be given instantly. The server is still the authority: this only
	 * skips the wait in the case where the page can see the answer, and anything
	 * it gets wrong is refused by the server exactly as before.
	 */
	if (haveAccount && creditsLeft === 0) {
		veilStuck(
			"Out of credits",
			"That is your credits spent. Top up and carry on: they do not expire, so whatever you buy is still there next month.",
			"Top up",
			"/account",
		);
		asking = false;
		drawButton.disabled = false;
		return;
	}

	openVeil(want);

	try {
		const { spec, repaired } = await requestDraw({
			want,
			view,
			motion,
			// The sprite on screen, sent as a style to copy rather than a thing to
			// edit. Its own bookkeeping fields are stripped: they say where it
			// belongs in somebody's project, which is not part of how it looks.
			like: matchOpen.checked ? tidy(readSpec() ?? ({} as SpriteSpec)) : null,
			image: shot,
			// Empty unless somebody paying has chosen one, in which case the server
			// checks that they are before honouring it.
			size: sizePick?.value || null,
		}, watchDraw);

		closeVeil();
		drawNote.dataset.state = "";
		drawNote.textContent = repaired
			? "Drawn, on the second try. The first attempt broke one of the rules, so it was shown the error and fixed it. It is on your shelf."
			: "Drawn, and added to your shelf. Try changing one of its colours below.";
		/*
		 * Not assembled again, if it was already watched being drawn.
		 *
		 * The workbench replays a sprite part by part when one arrives, which is
		 * the right introduction to a spec somebody opened from a file. After the
		 * overlay has just shown the same sprite building itself, it is the same
		 * show twice, and the second one is the one that delays the sprite being
		 * usable.
		 */
		if (!veilDrew()) startAssembly(spec.parts?.length ?? 0);
		setSpec(spec);
		shelve(spec, want);
		// The allowance just moved. Say so rather than counting down in the page,
		// which would be wrong the moment somebody draws in a second tab, and let
		// whatever displays it go and ask.
		document.dispatchEvent(new CustomEvent("bitwright:spent"));
	} catch (err) {
		const why = err instanceof Error ? err.message : "Something went wrong reaching the service.";
		// A turned-down subject is not an outage. The overlay comes down rather
		// than sitting there holding a sentence about the service, and the box is
		// reselected so the next attempt is one keystroke away.
		if (err instanceof DrawRefused && err.refused) {
			closeVeil();
			wantInput.select();
		} else if (err instanceof DrawRefused && err.fixable) {
			/*
			 * Running out is the product working, not the service breaking.
			 *
			 * This used to land in the same red failure state as an outage, so the
			 * one moment somebody is most willing to pay ended with a sentence and
			 * nowhere to go. The server's own words are kept, since it knows whether
			 * the answer is an account or a card, and a button is put under them.
			 */
			// Where to send them depends on whether there is an account to top up,
			// not on which refusal it was: a signed-in person out of credits and an
			// anonymous visitor out of free draws both arrive here as no_allowance.
			veilStuck(
				haveAccount ? "Out of credits" : "You need an account",
				why,
				haveAccount ? "Top up" : "Make an account",
				haveAccount ? "/account" : "/signin",
			);
		} else {
			veilFailed(why);
		}
		drawNote.dataset.state = "bad";
		drawNote.textContent = why;
	} finally {
		asking = false;
		drawButton.disabled = false;
	}
}

/**
 * An empty box draws whatever it is currently suggesting.
 *
 * Pressing the button and being told off for not having typed anything is a
 * poor first move. The placeholder is a real suggestion, so treat it as the
 * request and put it in the field so it is obvious what was asked for.
 */
function askForTyped() {
	const typed = wantInput.value.trim();
	const want = typed || wantInput.placeholder;
	if (!typed) {
		stopTyping();
		wantInput.value = want;
	}
	askFor(want);
}

drawButton.addEventListener("click", askForTyped);
wantInput.addEventListener("keydown", (e) => {
	if (e.key === "Enter") askForTyped();
});

// ---- the shelf ------------------------------------------------------------------

/**
 * Everything you can open, newest first.
 *
 * It is seeded with the bundled sprites so it is never an empty rack asking you
 * to be creative at it, and every sprite you draw is pushed onto the front. The
 * point is the second one: having somewhere for a sprite to go is most of what
 * makes people draw another.
 *
 * Sprites you drew are stored; the bundled ones are always re-added from the
 * build, so a change to them shows up rather than being pinned to whatever
 * shipped the first time you visited. `mine` is which is which.
 */
interface ShelfEntry {
	label: string;
	spec: SpriteSpec;
	mine: boolean;
}

function saveMine() {
	writeShelf(shelved.filter((e) => e.mine).slice(0, SHELF_MAX).map(({ label, spec }) => ({ label, spec })));
}

const shelved: ShelfEntry[] = [
	// Anything drawn on the welcome page is already here, because both pages read
	// the same key on the same origin. That is the whole handover.
	...readShelf().map((e) => ({ ...e, mine: true })),
	...Object.entries(EXAMPLES).map(([label, spec]) => ({ label, spec, mine: false })),
];
let openIndex = 0;

/*
 * SAVING WHAT YOU CHANGED.
 *
 * The Parts panel can now move, edit and delete, and until this there was
 * nowhere for any of it to go: the change was on screen and in the JSON, and a
 * refresh threw it away without asking. Two things fix that, and they only work
 * together. Something that saves, and something that stops the browser closing
 * over work that has not been.
 *
 * Dirty is the editor's text against the text as last opened or saved. Comparing
 * the parsed objects would need a deep compare and would call a sprite unchanged
 * after somebody reformatted it by hand, which is not what they would say.
 */
/** True when the open sprite differs from the last version written down. */
const unsaved = () => editor.value !== savedText;

/** The button says what it will do, which depends on whose sprite this is. */
function refreshSave() {
	const entry = shelved[openIndex];
	const dirty = unsaved();
	saveButton.disabled = !dirty;
	saveButton.dataset.dirty = dirty ? "1" : "0";
	saveButton.textContent = !dirty ? "Saved" : entry?.mine ? "Save" : "Save as yours";
	saveButton.title = !dirty
		? "Nothing has changed since this was last saved"
		: entry?.mine
			? "Write these changes to your shelf"
			: `${entry?.label ?? "This"} is a bundled example, so this saves a copy of it as yours`;
}

/** Called wherever the open sprite becomes the saved one. */
function markSaved() {
	savedText = editor.value;
	refreshSave();
}

function open(entry: ShelfEntry, index: number) {
	openIndex = index;
	drawNote.dataset.state = "";
	startAssembly(entry.spec.parts.length);
	setSpec(entry.spec);
	markSaved();
	buildShelf();
}

/**
 * Write the open sprite down.
 *
 * A bundled example is never overwritten. It ships with the build and is
 * re-added on every load, so "saving" one would produce a change that silently
 * vanished next time. Editing an example makes a new sprite of your own, which
 * is also the more useful reading of what somebody is doing when they open the
 * mug and start moving parts around.
 */
async function saveOpen() {
	const spec = readSpec();
	if (!spec) return fail("Fix the JSON before saving.");
	const entry = shelved[openIndex];

	if (entry?.mine) {
		shelved[openIndex] = { ...entry, spec };
		saveMine();
		buildShelf();
		markSaved();
		return;
	}

	const taken = new Set(shelved.map((e) => e.label));
	let suggested = `${spec.name} 2`;
	for (let n = 2; taken.has(suggested); n += 1) suggested = `${spec.name} ${n}`;

	const label = await askText({
		title: "Save as yours",
		body: `${entry?.label ?? "This"} is one of the bundled examples, so it stays as it is. This saves what you have made as a sprite of your own.`,
		label: "Call it",
		value: suggested,
		confirm: "Save it",
		check: (v) => (taken.has(v) ? "You already have one called that." : null),
	});
	if (!label) return;

	// Renamed on screen before it is shelved, so the editor and the shelf agree
	// and `shelve` records the right text as the saved one.
	const renamed = { ...spec, name: label };
	setSpec(renamed, true);
	shelve(renamed, label);
}

saveButton.addEventListener("click", () => void saveOpen());

/*
 * The browser's own "leave site?" prompt, which is the only one that can stop a
 * refresh. It only appears when there is something to lose.
 */
window.addEventListener("beforeunload", (e) => {
	if (!unsaved()) return;
	e.preventDefault();
	// Old browsers need the assignment; the text is the browser's, not ours.
	e.returnValue = "";
});

/** A caption in the shelf strip, naming the run of sprites after it. */
function shelfDivider(text: string) {
	const li = document.createElement("li");
	li.className = "shelf-split";
	li.textContent = text;
	return li;
}

/*
 * One row, or all of them.
 *
 * The shelf was a single strip that scrolled sideways with no scrollbar, so a
 * ninth sprite was simply not there as far as anybody could tell. Expanding
 * wraps it into rows and shows the lot. It is a toggle rather than always-on
 * because the height comes straight out of the stage, and most of the time you
 * are working on one sprite rather than looking for another.
 */
const shelfAll = $<HTMLButtonElement>("shelfall");
const shelfManage = $<HTMLDivElement>("shelfmanage");

shelfAll.addEventListener("click", () => {
	const open = shelf.dataset.all !== "1";
	shelf.dataset.all = open ? "1" : "0";
	shelfAll.setAttribute("aria-expanded", String(open));
	shelfAll.textContent = open ? "Show less" : "Show all";
	// Deleting the lot appears only here, where somebody is deliberately
	// managing the shelf rather than reaching past it for a sprite.
	shelfManage.hidden = !open || !shelved.some((e) => e.mine);
	// Bring the open sprite back into view: wrapping moves everything.
	shelf.querySelector('[aria-current="true"]')?.scrollIntoView({ block: "nearest", inline: "nearest" });
});

function buildShelf() {
	shelf.replaceChildren();

	/*
	 * Yours and the bundled ones, told apart.
	 *
	 * The only sign used to be a small dot in the corner, which meant nothing to
	 * anybody who had not been told. Now that editing a sprite saves a copy, the
	 * two sit next to each other constantly and the difference decides what the
	 * Save button will do, so the strip captions the two runs and tints yours.
	 * `shelved` is already yours-first, which is what makes one pass enough.
	 */
	let captioned = "";

	shelved.forEach((entry, i) => {
		const group = entry.mine ? "Yours" : "Examples";
		if (group !== captioned) {
			shelf.append(shelfDivider(group));
			captioned = group;
		}

		const li = document.createElement("li");
		const b = document.createElement("button");
		b.type = "button";
		// The checker belongs behind the sprite, not behind the whole card. It
		// used to run under the name too, which is where most of the noise came
		// from: seven tiles of grey chequerboard with grey text on top.
		b.className = "shelf-item";
		b.dataset.mine = entry.mine ? "1" : "0";
		if (i === openIndex) b.setAttribute("aria-current", "true");
		b.title = entry.label;
		b.setAttribute("aria-label", entry.mine ? `${entry.label}, yours` : entry.label);

		const c = document.createElement("canvas");
		try {
			// Keep the frames. Dropping them to get a still looks harmless and is
			// not: a part whose colour is driven by a frame throws the moment the
			// drive it names no longer exists, which silently emptied half the shelf.
			// Animated sprites nominate a still frame; the rest start at the first.
			const rendered = renderFrames(entry.spec);
			const frame = rendered[entry.spec.frames?.still ?? 0] ?? rendered[0];
			const scale = Math.max(1, Math.floor(48 / Math.max(frame.canvas.width, frame.canvas.height)));
			blitWithGrid(c, frame.canvas, scale);
		} catch {
			// A sprite that will not render keeps its slot but must not leave a
			// default-sized canvas sitting there as a phantom gap.
			c.width = 0;
			c.height = 0;
		}

		/*
		 * The name, under the picture.
		 *
		 * Sixteen pixel thumbnails of a mug and a battery are not as different as
		 * they look at 32, and half a shelf of things you drew yourself in one
		 * session look alike on purpose. Finding one meant hovering each in turn
		 * for a tooltip. It is clipped to one line and the whole name is still on
		 * the title, so a long one costs no layout.
		 */
		const named = document.createElement("span");
		named.className = "shelf-name";
		named.textContent = entry.label;

		const well = document.createElement("span");
		well.className = "shelf-cell checker";
		well.append(c);

		b.append(well, named);
		b.addEventListener("click", () => open(entry, i));
		li.append(b);

		// Only yours can be removed. The bundled ones come back from the build on
		// every load anyway, so a delete on them would silently undo itself.
		if (entry.mine) {
			const kill = document.createElement("button");
			kill.type = "button";
			kill.className = "kill";
			kill.textContent = "×";
			kill.title = `Remove ${entry.label}`;
			kill.setAttribute("aria-label", `Remove ${entry.label} from the shelf`);
			/*
			 * Asked first, because there is nothing to undo it with.
			 *
			 * A shelf sprite lives in this browser and nowhere else, so a × caught
			 * by a stray click took work away permanently and silently. The × is
			 * also small and sits on the corner of a thumbnail you were reaching
			 * for, which is exactly where a stray click happens.
			 */
			kill.addEventListener("click", async (e) => {
				e.stopPropagation();
				const sure = await askYesNo({
					title: `Delete ${entry.label}?`,
					body: "It only exists in this browser, so this cannot be undone. Anything you have already exported is untouched.",
					confirm: "Delete it",
					danger: true,
				});
				if (!sure) return;
				shelved.splice(i, 1);
				saveMine();
				// Where the bench goes next. Deleting the open sprite used to leave it
				// sitting there with no shelf row behind it, still editable and still
				// saveable, and deleting anything above it silently renumbered which
				// entry a save would write to.
				const landing = afterDelete(i, openIndex, shelved.length);
				openIndex = landing.index;
				if (landing.reopen && shelved[openIndex]) open(shelved[openIndex], openIndex);
				else buildShelf();
			});
			b.append(kill);
		}

		shelf.append(li);
	});
	const mine = shelved.filter((e) => e.mine).length;
	shelfCount.textContent = mine ? `${mine} yours · ${shelved.length}` : `${shelved.length}`;
	// Nothing of yours left to delete, so the button that deletes it goes too.
	if (!mine) shelfManage.hidden = true;
}

function shelve(spec: SpriteSpec, label: string) {
	shelved.unshift({ label, spec, mine: true });
	openIndex = 0;
	saveMine();
	buildShelf();
	// It has just been written down, so nothing is outstanding. Without this a
	// freshly drawn sprite would offer to save itself and warn on refresh.
	markSaved();
	shelf.scrollTo({ left: 0, behavior: reduced.matches ? "auto" : "smooth" });
}

/** A name nothing on the shelf is using, so neither route has to ask twice. */
function freeLabel(base: string): string {
	const taken = new Set(shelved.map((e) => e.label));
	if (!taken.has(base)) return base;
	for (let n = 2; ; n += 1) if (!taken.has(`${base} ${n}`)) return `${base} ${n}`;
}

/**
 * A blank canvas and your own parts.
 *
 * Everything else here starts from a sentence, which is the product, but it is
 * not the only reason somebody opens a tool like this: plenty of people already
 * know what they want to build and would rather stack the shapes themselves.
 *
 * It starts with one panel rather than nothing, because the format requires at
 * least one part and an empty stage with a validation error on it is a worse
 * first thirty seconds than a rectangle you can immediately move.
 */
function newSprite() {
	const name = freeLabel("untitled");
	const kitName = KITS.arcade ? "arcade" : Object.keys(KITS)[0];
	// Asked of the kit rather than named here. A hard-coded "casing" is not a
	// role the arcade kit has, so the blank sprite failed to draw and the panel
	// silently kept showing the sprite before it.
	const roles = Object.keys(KITS[kitName]?.roles ?? {});
	const role = roles.find((r) => r === "base") ?? roles.find((r) => r !== "none") ?? "outline";
	const blank = asSpec({
		name,
		size: [32, 32],
		kit: kitName,
		parts: [{ shape: "panel", inset: [10, 10, 10, 10], role }],
	});
	// No assembly animation: there is one part, and watching it appear teaches
	// nothing about a sprite you are about to build yourself.
	setSpec(blank);
	shelve(blank, name);
	$<HTMLButtonElement>("tab-parts").click();
	drawNote.dataset.state = "";
	drawNote.textContent = "A blank 32 by 32. Change the size below, then add shapes from the Shapes tab.";
}

$<HTMLButtonElement>("newsprite").addEventListener("click", newSprite);

/**
 * A spec from the clipboard.
 *
 * The Data panel already accepts a paste, but it replaces whatever is open,
 * which is the wrong thing to do to somebody who just wanted to look at a
 * sprite a colleague sent them. This puts it on the shelf as its own.
 *
 * It is checked by rendering rather than by parsing, because a spec can be
 * perfectly good JSON and still name a shape or a role that does not exist.
 */
$<HTMLButtonElement>("pastespec").addEventListener("click", async () => {
	const text = await askText({
		title: "Paste a sprite",
		body: "Any Bitwright spec: one you exported, one somebody sent you, or one you have been editing somewhere else.",
		label: "The JSON",
		placeholder: '{ "name": "lamp", "size": [32, 32], "kit": "arcade", "parts": [...] }',
		confirm: "Open it",
		multiline: true,
		check: (v) => {
			let parsed: unknown;
			try {
				parsed = JSON.parse(v);
			} catch (err) {
				return `That is not valid JSON. ${(err as Error).message}`;
			}
			try {
				renderFrames(parsed as SpriteSpec);
			} catch (err) {
				// The validator's own sentence, which names what would have been
				// allowed. Rewording it here would lose the useful half.
				return err instanceof Error ? err.message : "That did not draw.";
			}
			return null;
		},
	});
	if (!text) return;

	const spec = JSON.parse(text) as SpriteSpec;
	const label = freeLabel(spec.name || "pasted");
	const named = { ...spec, name: label };
	startAssembly(named.parts.length);
	setSpec(named);
	shelve(named, label);
	drawNote.dataset.state = "";
	drawNote.textContent = `Opened ${label}, and it is on your shelf.`;
});

// ---- the prompt suggests things by typing them ------------------------------------

/**
 * The placeholder types itself through the suggestions.
 *
 * An empty box with "describe a sprite" under it is a test. A box quietly
 * typing "a haunted toaster" is an invitation, and it answers the question
 * people actually have, which is not what the format allows but what is worth
 * asking for. It stops the moment anyone touches the field.
 */
const TYPE_MS = 55;
const HOLD_MS = 1700;
let typingStopped = false;

async function typeSuggestions() {
	if (reduced.matches) return;
	let n = 0;
	while (!typingStopped) {
		const phrase = SUGGESTIONS[n++ % SUGGESTIONS.length];
		for (let i = 1; i <= phrase.length && !typingStopped; i++) {
			wantInput.placeholder = phrase.slice(0, i);
			await new Promise((r) => setTimeout(r, TYPE_MS));
		}
		for (let waited = 0; waited < HOLD_MS && !typingStopped; waited += 100) {
			await new Promise((r) => setTimeout(r, 100));
		}
	}
}

function stopTyping() {
	if (typingStopped) return;
	typingStopped = true;
	wantInput.placeholder = "describe anything you like";
}

wantInput.addEventListener("focus", stopTyping);
wantInput.addEventListener("input", stopTyping);

// ---- controls ----------------------------------------------------------------------------

scaleInput.addEventListener("input", refreshView);

/*
 * The grid switch.
 *
 * aria-pressed rather than a class, because a button that toggles has a state
 * a screen reader should be able to hear, and the styling hangs off the same
 * attribute so the two can never disagree.
 */
const gridButton = $<HTMLButtonElement>("grid");
if (gridButton) {
	const showGrid = () => {
		gridButton.setAttribute("aria-pressed", grid ? "true" : "false");
		gridButton.title = grid
			? "Hide the pixel grid"
			: Number(scaleInput.value) < 4
				? "Show the pixel grid, which appears from 4x"
				: "Show the pixel grid";
	};
	showGrid();
	gridButton.addEventListener("click", () => {
		grid = !grid;
		localStorage.setItem("bitwright.grid", grid ? "1" : "0");
		showGrid();
		refreshView();
	});
	scaleInput.addEventListener("input", showGrid);
}

/*
 * The repeat switch.
 *
 * Same shape as the grid switch above, including aria-pressed carrying the
 * state rather than a class. The title changes with the spec because the button
 * answers a different question depending on it: with `tile` on it is "did the
 * join work", and with it off it is "here is the grid you would have shipped".
 */
const repeatButton = $<HTMLButtonElement>("repeat");
if (repeatButton) {
	const showRepeat = () => {
		repeatButton.setAttribute("aria-pressed", repeat ? "true" : "false");
		repeatButton.title = repeat
			? "Show one copy"
			: tiles3Hint()
				? "Show it repeated, to check the join"
				: "Show it repeated. This sprite has no tile set, so expect a seam";
	};
	showRepeat();
	repeatButton.addEventListener("click", () => {
		repeat = !repeat;
		localStorage.setItem("bitwright.repeat", repeat ? "1" : "0");
		showRepeat();
		refreshView();
	});
	editor.addEventListener("input", showRepeat);
}

/** Whether the spec currently in the editor asks for any edge to wrap. */
function tiles3Hint(): boolean {
	try {
		const t = (JSON.parse(editor.value) as { tile?: boolean | { x?: boolean; y?: boolean } }).tile;
		if (t === true) return true;
		return typeof t === "object" && t !== null && (t.x === true || t.y === true);
	} catch {
		return false;
	}
}
editor.addEventListener("input", () => {
	disabled = new Set();
	isolated = null;
	assembleTo = null;
	rerender();
});

playButton.addEventListener("click", () => {
	playing = !playing;
	playButton.textContent = playing ? "Pause" : "Play";
});

/** One place that turns bytes into a file, so every export behaves the same. */
function save(bytes: Uint8Array, filename: string, type: string) {
	const blob = new Blob([bytes as BlobPart], { type });
	const a = document.createElement("a");
	a.href = URL.createObjectURL(blob);
	a.download = filename;
	a.click();
	URL.revokeObjectURL(a.href);
}

/**
 * The two exports an animation is actually wanted for.
 *
 * A sheet is what a game engine slices: every frame side by side at native
 * size, no padding, because an engine steps along by frame width and a gap
 * would put a seam in each one. A GIF is what you paste somewhere to show
 * people, and it is exact rather than quantised, because these sprites are
 * already indexed colour.
 */
const sheetButton = $<HTMLButtonElement>("sheet");
const gifButton = $<HTMLButtonElement>("gif");

sheetButton.addEventListener("click", () => {
	const spec = readSpec();
	if (!spec || frames.length < 2) return;
	save(encodePng(spriteSheet(frames.map((f) => f.canvas.toBitmap()))), `${spec.name}_sheet.png`, "image/png");
	drawNote.dataset.state = "";
	drawNote.textContent = `Saved ${frames.length} frames side by side. Each frame is ${spec.size[0]} by ${spec.size[1]}.`;
});

gifButton.addEventListener("click", () => {
	const spec = readSpec();
	if (!spec || frames.length < 2) return;
	const gif = encodeGif(
		frames.map((f) => f.canvas.toBitmap()),
		{ delayMs: 125 },
	);
	if (!gif) {
		// Refused rather than quantised, so say which of the two reasons it was.
		drawNote.dataset.state = "bad";
		drawNote.textContent = "This one needs more than 256 colours, which a GIF cannot hold. The sheet has every colour.";
		return;
	}
	save(gif, `${spec.name}.gif`, "image/gif");
	drawNote.dataset.state = "";
	drawNote.textContent = "Saved as a looping GIF, with the sprite's exact colours.";
});

$<HTMLButtonElement>("download").addEventListener("click", () => {
	const f = frames[current];
	const spec = readSpec();
	if (!f || !spec) return;
	const file = frames.length === 1 ? `${spec.name}.png` : `${spec.name}_${current + 1}.png`;
	const blob = new Blob([encodePng(f.canvas.toBitmap()) as BlobPart], { type: "image/png" });
	const a = document.createElement("a");
	a.href = URL.createObjectURL(blob);
	a.download = file;
	a.click();
	URL.revokeObjectURL(a.href);
});

$<HTMLButtonElement>("copy").addEventListener("click", async () => {
	await navigator.clipboard.writeText(editor.value);
	const b = $<HTMLButtonElement>("copy");
	b.textContent = "Copied";
	setTimeout(() => (b.textContent = "Copy JSON"), 1200);
});

// ---- go ---------------------------------------------------------------------------------

// ---- the inspector's tabs ------------------------------------------------------

/**
 * One panel at a time, so the window itself never scrolls.
 *
 * Four panels stacked would put Shapes a screen and a half below Parts, which
 * is the scrolling problem this layout exists to avoid.
 */
const TABS = ["parts", "colours", "data", "shapes"] as const;

for (const name of TABS) {
	$<HTMLButtonElement>(`tab-${name}`).addEventListener("click", () => {
		for (const other of TABS) {
			const on = other === name;
			$<HTMLButtonElement>(`tab-${other}`).setAttribute("aria-selected", String(on));
			$<HTMLDivElement>(`pane-${other}`).hidden = !on;
		}
	});
}

/**
 * The status line is the way to the full error.
 *
 * A validator message names every role or shape that would have been accepted,
 * so it can run to several lines. Left to wrap it would grow the toolbar and
 * shrink the stage every time somebody typo'd. It is clipped to one line here
 * and clicking it opens the Data panel, where the whole thing is shown.
 */
status.addEventListener("click", () => {
	if (status.dataset.state === "bad") $<HTMLButtonElement>("tab-data").click();
});

$<HTMLButtonElement>("clearshelf").addEventListener("click", async () => {
	const mine = shelved.filter((e) => e.mine).length;
	if (!mine) return;
	const sure = await askYesNo({
		title: `Clear ${mine} sprite${mine === 1 ? "" : "s"}?`,
		body: "This removes the ones you drew from this browser. The bundled examples stay. Anything you have already exported is untouched.",
		confirm: "Clear the shelf",
		danger: true,
	});
	if (!sure) return;
	shelved.splice(0, shelved.length, ...shelved.filter((e) => !e.mine));
	openIndex = 0;
	saveMine();
	buildShelf();
});

// The shape browser, handed the five things it needs of the open sprite.
buildReference({
	readSpec,
	kitOf,
	setSpec,
	fail,
	setEditing: (index) => {
		editing = index;
	},
});
buildShelf();
void setUpBotCheck(document.getElementById("botcheck"));

// ---- who is signed in ----------------------------------------------------------------

/**
 * The one thing about the account this page still has to know.
 *
 * Saying who you are and what is left is the shared bar's job now, up in
 * chrome.ts. What is left here is whether to show the size chooser, which only
 * a paid account gets and which lives among the prompt's own controls rather
 * than in the bar.
 *
 * Asked separately rather than passed down from the bar. It is one cached
 * request either way, and the alternative is the bar reaching into the
 * workbench's markup to toggle a control it knows nothing about.
 */
let haveAccount = false;

/*
 * The one control that depends on the plan, kept in step with the bar.
 *
 * This used to fetch /api/me itself after every draw and read one field off it,
 * while the credit count in the bar was painted once at page load and never
 * again. So the balance was being re-read and discarded on exactly the event
 * that changed it, and the number in the corner went stale until a refresh.
 *
 * Now the bar owns the request and says what came back. This listens. One
 * request, and the two things that depend on it cannot disagree.
 */
let creditsLeft: number | null = null;

function showAccount(me: { signedIn?: boolean; plan?: string; credits?: number }) {
	// Naming the canvas is part of what paying buys, so the control only appears
	// for an account that has. The server checks the same thing.
	haveAccount = me.signedIn === true;
	creditsLeft = typeof me.credits === "number" ? me.credits : null;
	const sizeGroup = document.getElementById("sizegroup");
	if (sizeGroup) sizeGroup.hidden = me.plan !== "paid";
}

document.addEventListener("bitwright:me", (e) => {
	showAccount((e as CustomEvent<{ signedIn?: boolean; plan?: string; credits?: number }>).detail ?? {});
});

startAssembly(EXAMPLES.mug.parts.length);
setSpec(EXAMPLES.mug);
// The first sprite arrives here rather than through `open`, so without this the
// page would load already claiming unsaved changes and warn on the first
// refresh, having changed nothing.
markSaved();
void typeSuggestions();

let lastFrameAt = 0;
function loop(now: number) {
	// Everything here is wrapped, because this loop is the one place where a
	// thrown error is unrecoverable: the exception escapes, the next frame is
	// never requested, and the page looks frozen with no clue why. A single bad
	// render should cost one frame, not the session.
	try {
		if (assembleTo !== null && now >= assembleNext) {
			const spec = readSpec();
			const total = spec?.parts.length ?? 0;
			const done = assembleTo + 1 >= total;
			assembleTo = done ? null : assembleTo + 1;
			assembleNext = now + STEP_MS;
			if (spec && done) {
				// A full rerender so the caption, the frame strip and the export
				// buttons all describe the finished sprite rather than the last
				// half-built step.
				rerender(false);
			} else if (spec) {
				frames = renderFrames(effectiveSpec(spec));
				markAssembly();
				// Say what is happening. Watching parts appear with no caption reads
				// as the page having stalled rather than as it building something.
				status.dataset.state = "";
				status.textContent = `${spec.name} · building part ${assembleTo} of ${total}`;
				refreshView();
			}
		}
		if (assembleTo === null && playing && frames.length > 1 && now - lastFrameAt > 125) {
			lastFrameAt = now;
			current = (current + 1) % frames.length;
			refreshView();
		}
	} catch (err) {
		// Stop assembling rather than retry the same failing render every frame.
		assembleTo = null;
		fail(`Renderer error: ${(err as Error).message}`);
	}
	requestAnimationFrame(loop);
}
requestAnimationFrame(loop);
